import { ref } from 'vue'
import axios from 'axios'

import { env } from '@js/utils/env'
import { log } from '@js/utils/logger'

export default function useRecaptcha() {
  const isRecaptchaLoaded = ref(false)
  const isValidatingRecaptcha = ref(false)

  /**
   * It's necessary to include the CSRF token set by Craft in requests to Craft API routes.
   * This function retrieves the CSRF token from a meta tag that was specifically added to
   * the templates/_layouts/skeleton.twig file in order to enable retrieving the value.
   */
  const getCsrfToken = (): string | null | undefined => {
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

    if (!token) {
      log({
        message: 'Craft CSRF token was not found',
        file: 'src/wcei/js/composables/useRecaptcha.ts',
      })
    }

    return token
  }

  const loadRecaptcha = (): Promise<void> => {
    return new Promise((resolve) => {
      if (window.grecaptcha?.ready) {
        isRecaptchaLoaded.value = true

        resolve()

        return
      }

      const script = document.createElement('script')

      script.src = `https://www.google.com/recaptcha/api.js?render=${env.recaptcha.SITE_KEY}`
      script.async = true
      script.defer = true

      script.onload = () => {
        isRecaptchaLoaded.value = true

        if (window.grecaptcha?.ready) {
          window.grecaptcha.ready(() => resolve())
        }
      }

      document.head.appendChild(script)
    })
  }

  /**
   * Validates the reCAPTCHA token with the backend module modules/recaptcha.
   */
  const validateRecaptcha = async (token: string): Promise<boolean> => {
    try {
      isValidatingRecaptcha.value = true

      const csrfToken = getCsrfToken()
      if (!csrfToken) {
        log({
          file: 'src/wcei/js/composables/useRecaptcha.ts',
          message: 'User could not be validated because the CSRF token was not found',
        })
        return false
      }

      const { data } = await axios.post(
        '/actions/recaptcha/recaptcha/verify',
        { recaptchaToken: token },
        {
          headers: {
            'X-CSRF-Token': csrfToken,
          },
        },
      )

      return data.success === true
    } catch (error) {
      log({
        message: `Error validating reCAPTCHA: ${error}`,
        file: 'src/wcei/js/composables/useRecaptcha.ts',
      })
      return false
    } finally {
      isValidatingRecaptcha.value = false
    }
  }

  /**
   * Returns a reCAPTCHA token for the given action.
   */
  const getRecaptchaToken = async (action = 'register'): Promise<string | null> => {
    try {
      if (!isRecaptchaLoaded.value) {
        await loadRecaptcha()
      }

      if (!window.grecaptcha?.execute) {
        return null
      }

      return await window.grecaptcha.execute(env.recaptcha.SITE_KEY, { action })
    } catch (error) {
      log({
        message: `Error obtaining reCAPTCHA token: ${error}`,
        file: 'src/wcei/js/composables/useRecaptcha.ts',
      })
      return null
    }
  }

  return {
    loadRecaptcha,
    validateRecaptcha,
    isValidatingRecaptcha,
    getRecaptchaToken,
  }
}
